import { createContext, useContext, useMemo } from 'react';
import type { AuthUser } from '@/lib/api/auth';

export interface AuthStateContextType {
  user: AuthUser | null;
  token: string | null;
  isLoading: boolean;
  loading: boolean;
  isAuthenticated: boolean;
}

export interface AuthActionsContextType {
  login: (email: string, password: string, rememberMe: boolean) => Promise<{ success: boolean; user: AuthUser }>;
  register: (
    username: string,
    email: string,
    password: string,
    termsAccepted: boolean,
    referredBy?: string
  ) => Promise<{ success: boolean; user: AuthUser }>;
  logout: () => void;
  refreshSessionState: () => Promise<void>;
  updateUserBalance: (newBalance: number) => void;
  refreshTokenOnActivity: () => Promise<void>;
}

interface AuthContextType extends AuthStateContextType, AuthActionsContextType {}

export const AuthStateContext = createContext<AuthStateContextType | undefined>(undefined);
export const AuthActionsContext = createContext<AuthActionsContextType | undefined>(undefined);

export function useAuthState(): AuthStateContextType {
  const context = useContext(AuthStateContext);
  if (context === undefined) {
    throw new Error('useAuthState must be used within an AuthProvider');
  }
  return context;
}

export function useAuthActions(): AuthActionsContextType {
  const context = useContext(AuthActionsContext);
  if (context === undefined) {
    throw new Error('useAuthActions must be used within an AuthProvider');
  }
  return context;
}

export function useAuth(): AuthContextType {
  const state = useAuthState();
  const actions = useAuthActions();
  return useMemo(() => ({ ...state, ...actions }), [state, actions]);
}
